import React, { useEffect, useMemo, useState } from 'react';
import {
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  ListGroupItemHeading,
  ListGroupItemText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { ModelDefinitionDto, ModelTypes, RepositoryDto } from '../../models/APIModels';
import { Controller, useForm } from 'react-hook-form';
import { ApiHelper } from '../../common/ApiHelper';
import Loader from '../Loader';
import { useSelector } from 'react-redux';
import { userInfoSelector } from '../../store/slices/userInfo';
import { ModelForm } from '../ModelEditor/ModelForm';
import Select from 'react-select';
import useAppUtils from '../../Utils/useAppUtils';
import { ValidationErrors } from '../../models/ValidationErrors';
import { ValidationError } from '../../models/ValidationError';
import Button from '../Button';
import { MODEL_LIST } from '../../constants';
import Progress from '../Loaders/Progress';

interface IProps {
  open: boolean;
  close: () => void;
  onSave: (model: ModelDefinitionDto) => void;
  isSaving: boolean;
}

const CreateModelModal = ({ open, close, onSave, isSaving }: IProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo } = useSelector(userInfoSelector);
  const [repositoryList, setRepositoryList] = useState<RepositoryDto[]>([]);
  const [parentRepository, setParentRepository] = useState();
  const { register, control, setValue, handleSubmit, reset } = useForm<ModelForm>({});
  const [activeModel, setActiveModel] = useState(ModelTypes.HostedModel);
  const [errors, setErrors] = useState(new ValidationErrors([]));
  const { alert, confirm } = useAppUtils();
  const [showServiceProvideUrl, setShowServiceProviderUrl] = useState(false);
  const [repositoryItem, setRepositoryItem] = useState();
  const [permittedAppFeatures, setPermittedAppFeatures] = useState([]);

  const handleRepositoryChange = async (repo: any) => {
    // console.log(repo);
    if (activeModel === ModelTypes.IndexedService || activeModel === ModelTypes.HostedModel) {
      const response = await ApiHelper.GetRepositoryById(repo.repositoryId, true);
      if (response.data) {
        setRepositoryItem(response.data);
        const repositoryAccessRight = response.data.repositoryAccessRights.find((repo) => repo.userId === userInfo?.userId);
        setShowServiceProviderUrl(repositoryAccessRight?.roles.includes('SuperAdmin'));
      } else {
        setShowServiceProviderUrl(false);
      }
    }
    setValue('parentRepositoryId', repo.value);
  };
  const onModelListItemClick = (m) => {
    if (m.id === ModelTypes.HostedModel && !permittedAppFeatures.includes('UnityPredict')) {
      return;
    }
    setActiveModel(m.id);
    setValue('modelType', m.id);
    if (repositoryItem && m.id === ModelTypes.IndexedService) {
      const repositoryAccessRight = repositoryItem.repositoryAccessRights.find((repo) => repo.userId === userInfo?.userId);
      setShowServiceProviderUrl(repositoryAccessRight?.roles.includes('SuperAdmin'));
    } else {
      setShowServiceProviderUrl(false);
    }
  };

  const validateForm = (form: ModelForm) => {
    const errors: ValidationError[] = [];
    if (form && !form.modelName && form.websiteUrl === '') {
      errors.push({ fieldId: 'modelName', message: 'Model Name is required.' });
    }
    // if (form && !form.modelType) {
    //   errors.push({ fieldId: 'modelType', message: 'Model Type is required.' });
    // }
    if (form && !form.parentRepositoryId) {
      errors.push({ fieldId: 'parentRepositoryId', message: 'Parent Repository is required.' });
    }
    setErrors(new ValidationErrors(errors));
    return new ValidationErrors(errors);
  };

  const onSubmit = async (data: ModelForm) => {
    // console.log('Create model data', data);
    const errors = validateForm(data);
    if (errors.hasAny()) {
      const message = 'Please fix validation errors before continuing: ' + errors.errors[0].message;
      alert(message, 'error');
      return;
    }
    onSave(data);
  };
  const init = async () => {
    try {
      setPermittedAppFeatures(userInfo?.permittedAppFeatures);
      setActiveModel(userInfo?.permittedAppFeatures.includes('UnityPredict') ? ModelTypes.HostedModel : ModelTypes.IndexedModel);
      setIsLoading(true);
      if (userInfo) {
        const form = ModelForm.CreateForm(userInfo?.publicName, userInfo?.userId);
        reset(form);
      }
      const response = await ApiHelper.GetRepositories();
      if (response && response.data) {
        const list :any = [];
         response.data.forEach((item) => {
          if(item.permissions.includes("Write")){
            list.push(Object.assign({}, item, { value: item.repositoryId, label: item.repositoryName }))
          }
        });
        setRepositoryList(list);
        setIsLoading(false);
      }
    } catch (ex) {
      console.error('Unable to get data', ex);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    // console.log(userInfo);

    init();
  }, []);
  return (
    <Modal isOpen={open} fade={false} toggle={close} size="lg">
      {isLoading && <Progress  progressLabel='Please Wait ...'/>}

      <Form className="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader toggle={close}>Create Model</ModalHeader>
        <ModalBody>
          <Controller
            control={control}
            name="modelName"
            render={({ field: { ref, ...fieldProps } }) => (
              <FormGroup>
                <Label for="modelName">Model Name</Label>
                <Input
                  type="text"
                  id="modelName"
                  innerRef={ref}
                  {...fieldProps}
                  invalid={errors && errors.has && errors.has('modelName')}
                />
                <FormFeedback>
                  {errors && errors.has && errors.has('modelName') && <span>{errors.message('modelName')}</span>}
                </FormFeedback>
              </FormGroup>
            )}
          />

          <ListGroup>
            {MODEL_LIST.map((m) => {
              return (
                <ListGroupItem
                  key={m.id}
                  active={activeModel === m.id}
                  onClick={(e) => onModelListItemClick(m)}
                  style={{
                    background: m.id === ModelTypes.HostedModel && !permittedAppFeatures.includes('UnityPredict') ? '#E8E6E6' : '',
                    color: m.id === ModelTypes.HostedModel && !permittedAppFeatures.includes('UnityPredict') ? '#6c757d' : '',
                  }}
                >
                  <ListGroupItemHeading style={{ marginBottom: 'opx !important' }}>{m.name}</ListGroupItemHeading>
                  <ListGroupItemText style={{ marginBottom: 'opx !important' }}>
                    {m.id === ModelTypes.HostedModel && !permittedAppFeatures.includes('UnityPredict') && (
                     <span>
                      {m.description}
                      <br />
                      <a
                        target="_blank"
                        style={{ color: '#0d6efd', fontFamily: 'normal', fontStyle: 'italic', textDecoration: 'none' }}
                        href="https://unitypredict.com/sign-up-beta"
                        className="cursor-pointer"
                      >
                      {m.linkText}
                      </a>
                     </span>
                    )}
                    {(m.id !== ModelTypes.HostedModel ||
                      (m.id === ModelTypes.HostedModel && permittedAppFeatures.includes('UnityPredict'))) &&
                      m.description}
                    
                    </ListGroupItemText>
                </ListGroupItem>
              );
            })}
            <FormFeedback>
              {errors && errors.has && errors.has('modelType') && <span>{errors.message('modelType')}</span>}
            </FormFeedback>
          </ListGroup>
          <FormGroup style={{ marginTop: '10px' }}>
            <Label for="parentRepository">Parent Repository</Label>
            <Select options={repositoryList} onChange={handleRepositoryChange} defaultValue={parentRepository} />
            <FormFeedback>
              {errors && errors.has && errors.has('parentRepositoryId') && <span>{errors.message('parentRepositoryId')}</span>}
            </FormFeedback>
          </FormGroup>
          {showServiceProvideUrl && (activeModel === ModelTypes.IndexedService || activeModel === ModelTypes.HostedModel) && (
            <>
              <Controller
                control={control}
                name="websiteUrl"
                render={({ field: { ref, ...fieldProps } }) => (
                  <FormGroup>
                    <Label for="websiteUrl">Service Provider Website Url (optional)</Label>
                    <Input
                      type="text"
                      id="websiteUrl"
                      innerRef={ref}
                      {...fieldProps}
                      // invalid={errors && errors.has && errors.has('websiteUrl')}
                    />
                  </FormGroup>
                )}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="contained" rounded type="submit" disabled={isSaving}>
            Submit
          </Button>
          <Button variant="contained" rounded onClick={close} disabled={isSaving}>
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateModelModal;
